import React from 'react';
import { cn } from '../../../utils/cn';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'destructive';
  /** Button size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Icon to display */
  icon: React.ReactNode;
  /** Accessible label for screen readers */
  'aria-label': string;
}

const iconButtonVariants = {
  primary: [
    'bg-primary-600 text-neutral-50 border-primary-600',
    'hover:bg-primary-700 hover:border-primary-700',
    'active:bg-primary-800 active:border-primary-800',
    'focus:ring-primary-500/25',
    'disabled:bg-primary-300 disabled:border-primary-300 disabled:text-neutral-50/80',
  ],
  secondary: [
    'bg-neutral-100 text-neutral-900 border-neutral-200',
    'hover:bg-neutral-200 hover:border-neutral-300',
    'active:bg-neutral-300 active:border-neutral-400',
    'focus:ring-neutral-500/20',
    'disabled:bg-neutral-50 disabled:text-neutral-400 disabled:border-neutral-100',
    'dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700',
    'dark:hover:bg-neutral-700 dark:hover:border-neutral-600',
    'dark:active:bg-neutral-600 dark:active:border-neutral-500',
    'dark:disabled:bg-neutral-900 dark:disabled:text-neutral-600 dark:disabled:border-neutral-800',
  ],
  tertiary: [
    'bg-transparent text-primary-600 border-primary-500',
    'hover:bg-primary-50 hover:text-primary-700',
    'active:bg-primary-100 active:text-primary-800',
    'focus:ring-primary-500/20',
    'disabled:text-primary-300 disabled:border-primary-200',
    'dark:text-primary-400 dark:border-primary-600',
    'dark:hover:bg-primary-950 dark:hover:text-primary-300',
    'dark:active:bg-primary-900 dark:active:text-primary-200',
    'dark:disabled:text-primary-700 dark:disabled:border-primary-800',
  ],
  ghost: [
    'bg-transparent text-neutral-700 border-transparent',
    'hover:bg-neutral-100 hover:text-neutral-900',
    'active:bg-neutral-200',
    'focus:ring-neutral-500/20',
    'disabled:text-neutral-400',
    'dark:text-neutral-300',
    'dark:hover:bg-neutral-800 dark:hover:text-neutral-100',
    'dark:active:bg-neutral-700',
    'dark:disabled:text-neutral-600',
  ],
  destructive: [
    'bg-error-600 text-neutral-50 border-error-600',
    'hover:bg-error-700 hover:border-error-700',
    'active:bg-error-800 active:border-error-800',
    'focus:ring-error-500/25',
    'disabled:bg-error-300 disabled:border-error-300 disabled:text-neutral-50/80',
  ],
};

const iconButtonSizes = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-3',
  xl: 'p-4',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'font-medium leading-none',
          'border rounded-lg',
          'transition-all duration-normal ease-luxury',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-60',
          // Variant styles
          iconButtonVariants[variant],
          // Size styles
          iconButtonSizes[size],
          // Custom className
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          icon
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
